import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/cn.js';

export type BadgeTone =
  | 'neutral'
  | 'amber'
  | 'red'
  | 'green'
  | 'blue'
  | 'violet'
  | 'yellow'
  | 'slate';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  dot?: boolean;
}

const tones: Record<BadgeTone, string> = {
  neutral: 'bg-ink-700 text-ink-200 border-ink-600',
  amber: 'bg-beacon-400/10 text-beacon-300 border-beacon-400/30',
  red: 'bg-danger-500/15 text-danger-400 border-danger-500/30',
  green: 'bg-ok-500/15 text-ok-400 border-ok-500/30',
  blue: 'bg-info-500/15 text-info-400 border-info-500/30',
  violet: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30',
  yellow: 'bg-warn-500/15 text-warn-400 border-warn-500/30',
  slate: 'bg-ink-600/50 text-ink-200 border-ink-500',
};

const dotColors: Record<BadgeTone, string> = {
  neutral: 'bg-ink-300',
  amber: 'bg-beacon-400',
  red: 'bg-danger-500',
  green: 'bg-ok-500',
  blue: 'bg-info-500',
  violet: 'bg-indigo-400',
  yellow: 'bg-warn-500',
  slate: 'bg-ink-300',
};

export function Badge({
  tone = 'neutral',
  dot = false,
  className,
  children,
  ...rest
}: BadgeProps) {
  return (
    <span
      className={cn(
        // INTERACTION_SPEC §5 — state-change budget 200ms. Tone is the
        // only thing that changes at runtime on a Badge (status,
        // priority, contract status all swap tones on mutation), so a
        // colour-only transition is the right scope. The dot inherits
        // via its own transition class below.
        'inline-flex items-center gap-1 px-1.5 h-[20px] rounded-[5px] text-[11px] font-medium border leading-none transition-colors duration-200',
        tones[tone],
        className
      )}
      {...rest}
    >
      {dot && <span className={cn('w-1.5 h-1.5 rounded-full transition-colors duration-200', dotColors[tone])} />}
      {children}
    </span>
  );
}
